import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { EmailIcon, PhoneIcon, UserAddressIcon, UserIcon } from "assets/icons";
import { getIn, useFormik } from "formik";
import { getMe } from "stores/slices/auth";
import {
  isSaving,
  updateWorkspace,
  WorkspaceInfo,
} from "stores/slices/workspace";
import { YupValidations } from "utils/validation";
import * as Yup from "yup";

import AppButton from "components/button";
import FormInput from "components/forms/components/formInput";
import ModalForm from "components/forms/components/modalForm";

const WorkspaceProfileForm = props => {
  const { isOpenEdit, setIsOpenEdit } = props;

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const workspaceInfo = useSelector(WorkspaceInfo);

  const loading = useSelector(isSaving);

  const validationSchema = Yup.object().shape({
    workspaceName: YupValidations.requiredString,
    address: YupValidations.requiredString,
    contactName: YupValidations.requiredString,
    contactNumber: Yup.string()
      .trim()
      .required("validate.required")
      .min(6, "validate.min_length_6")
      .max(45, "validate.max_length_45"),
    contactEmail: YupValidations.email,
    contactInvoices: YupValidations.email,
  });

  const onSubmit = async data => {
    if (data) {
      const request = {
        name: data.workspaceName,
        phoneNumber: data.contactNumber,
        address: data.address,
        contactName: data.contactName,
        contactEmail: data.contactEmail,
        invoiceEmail: data.contactInvoices,
      };
      const result = await dispatch(
        updateWorkspace({ ...request, id: workspaceInfo.id })
      );
      if (result && result.payload.status === 200) {
        setIsOpenEdit(false);
        dispatch(getMe());
      }
    }
  };

  const handleForm = useFormik({
    initialValues: {
      workspaceName: workspaceInfo.name || "",
      address: workspaceInfo.address || "",
      contactName: workspaceInfo.contactName || "",
      contactNumber: workspaceInfo.phoneNumber || "",
      contactEmail: workspaceInfo.contactEmail || "",
      contactInvoices: workspaceInfo.invoiceEmail || "",
    },
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  });

  const { values, setFieldValue, handleSubmit, setValues, resetForm } =
    handleForm;

  const inputItems = [
    {
      type: "text",
      key: "workspaceName",
      label: t("label.workspaceName"),
      placeholder: "label.workspaceName",
      icon: <UserIcon />,
      span: 24,
      required: true,
    },
    {
      type: "textarea",
      key: "address",
      label: t("workspace.companyAddress"),
      placeholder: "workspace.companyAddress",
      icon: <UserAddressIcon />,
      span: 24,
      required: true,
    },
    {
      type: "text",
      key: "contactName",
      label: t("label.contactName"),
      placeholder: "label.contactName",
      icon: <UserIcon />,
      span: 24,
      required: true,
    },
    {
      type: "text",
      key: "contactNumber",
      label: t("label.contactNumber"),
      placeholder: "text.userPhoneNumber",
      icon: <PhoneIcon />,
      span: 24,
      required: true,
    },
    {
      type: "text",
      key: "contactEmail",
      label: t("label.contactEmail"),
      placeholder: "label.contactEmail",
      icon: <EmailIcon />,
      span: 24,
      required: true,
    },
    {
      type: "text",
      key: "contactInvoices",
      label: t("label.contactInvoices"),
      placeholder: "label.contactInvoices",
      icon: <EmailIcon />,
      span: 24,
      required: true,
    },
  ];

  useEffect(() => {
    if (workspaceInfo) {
      setValues({
        ...workspaceInfo,
        workspaceName: workspaceInfo.name || "",
        address: workspaceInfo.address || "",
        contactName: workspaceInfo.contactName || "",
        contactNumber: workspaceInfo.phoneNumber || "",
        contactEmail: workspaceInfo.contactEmail || "",
        contactInvoices: workspaceInfo.invoiceEmail || "",
      });
    }
  }, [isOpenEdit, workspaceInfo]);

  return (
    <ModalForm
      title={t("title.updateWorkspaceProfile")}
      onCancel={() => setIsOpenEdit(false)}
      isOpen={isOpenEdit}
      labelSubmit={t("label.save")}
      customWidth={700}
      footer={false}
      maskClosable={false}
    >
      <div style={{ height: "100%" }}>
        <FormInput
          items={inputItems.map(item => {
            item.label = t(item.label);
            if (item.key) {
              item.value = getIn(values, item.key);
            }
            return { ...item };
          })}
          form={handleForm}
          onChange={setFieldValue}
          layout={"vertical"}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            marginTop: "8px",
          }}
        >
          <AppButton
            label={t("label.cancel")}
            color={"grey"}
            loading={loading}
            onClick={() => {
              setIsOpenEdit(false);
              setValues(
                workspaceInfo ? workspaceInfo : handleForm.initialValues
              );
              resetForm();
            }}
            type="button"
            size={"minimum"}
            className={"cancelButton"}
          />
          <AppButton
            label={t("label.save")}
            color={"blue"}
            loading={loading}
            type="submit"
            onClick={handleSubmit}
            size={"minimum"}
          />
        </div>
      </div>
    </ModalForm>
  );
};

export default WorkspaceProfileForm;
